<script lang="ts">
  import { faCode } from '@fortawesome/free-solid-svg-icons';
  import type { Measurement } from '$lib/core/geom';
  import {
    formatArea,
    formatDecimal,
    formatLength,
    type NodeId,
    type Style,
  } from '$lib/core/model';
  import { describeObject, type ObjectProperties, type StyleProperty } from '$lib/core/scene';
  import type { EditorState } from '../editor-state.svelte';
  import Icon from './Icon.svelte';

  const { editor, onShowSource }: { editor: EditorState; onShowSource: (id: NodeId) => void } =
    $props();

  const selected = $derived(
    editor.selection.flatMap((id) => describeObject(editor.document, editor.scene, id) ?? []),
  );

  const STYLE_LABELS: Record<keyof Style, string> = {
    color: 'Color',
    thick: 'Thickness',
    line: 'Line type',
    textHeight: 'Text height',
    arrowSize: 'Arrow size',
    arrow: 'Arrow',
  };

  function show(measurement: Measurement): string {
    switch (measurement.kind) {
      case 'point':
        return `${formatLength(measurement.value.x, editor.lengths)}, ${formatLength(measurement.value.y, editor.lengths)}`;
      case 'angle':
        return `${formatDecimal(measurement.value, editor.lengths.precision)}°`;
      case 'area':
        return formatArea(measurement.value, editor.lengths);
      default:
        return formatLength(measurement.value, editor.lengths);
    }
  }

  const showStyle = ({ value }: StyleProperty): string =>
    typeof value === 'number' ? formatLength(value, editor.lengths) : String(value);

  function stateOf(object: ObjectProperties): string {
    const words = [object.visible ? 'shown' : 'hidden', ...(object.locked ? ['locked'] : [])];
    const text = words.join(', ');
    return text[0]!.toUpperCase() + text.slice(1);
  }
</script>

{#snippet table(title: string, rows: readonly Measurement[])}
  <dl aria-label="Measurements of {title}">
    {#each rows as measurement (measurement.label)}
      <dt>{measurement.label}</dt>
      <dd>{show(measurement)}</dd>
    {/each}
  </dl>
{/snippet}

<!-- Picked points and selected shapes share one table, with the same rows for a line. -->
{#if editor.activeToolId === 'measure'}
  {#if editor.toolMeasurements.length > 0}
    <section class="object panel-item" aria-label="Measurements of picked points">
      <h3>Picked points</h3>
      {@render table('picked points', editor.toolMeasurements)}
    </section>
  {:else}
    <p class="empty">Pick points on the drawing to measure between them.</p>
  {/if}
{:else if selected.length === 0}
  <p class="empty">Select an object to see its properties.</p>
{:else}
  {#each selected as object (object.id)}
    {@const line = editor.sourceLineOf(object.id)}
    <section class="object panel-item" aria-label="Properties of {object.name}">
      <h3>{object.name} <span class="kind">{object.kind}</span></h3>
      <dl>
        <dt>Layer</dt>
        <dd>{object.layer}</dd>
        <dt>State</dt>
        <dd>{stateOf(object)}</dd>
        {#if object.views.length > 0}
          <dt>View</dt>
          <dd>{object.views.join(', ')}</dd>
        {/if}
        {#if object.groups.length > 0}
          <dt>Group</dt>
          <dd>{object.groups.join(' in ')}</dd>
        {/if}
        {#each object.style as property (property.key)}
          <dt>{STYLE_LABELS[property.key]}</dt>
          <dd>
            {#if property.key === 'color'}
              <span class="swatch" style:background={String(property.value)}></span>
            {/if}
            {showStyle(property)}
            <span class="from">{property.from}</span>
          </dd>
        {/each}
        {#if line !== null}
          <dt>Source</dt>
          <dd>
            <button
              type="button"
              class="source"
              title="Edit the script line that drew it"
              onclick={() => onShowSource(object.id)}
            >
              <Icon icon={faCode} />line {line}
            </button>
          </dd>
        {/if}
        {#if object.error}
          <dt>Error</dt>
          <dd class="error">{object.error}</dd>
        {/if}
      </dl>
      {#if object.measurements.length > 0}
        <h4>Measurements</h4>
        {@render table(object.name, object.measurements)}
      {/if}
    </section>
  {/each}
{/if}

<style>
  .empty {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--color-text-muted);
  }

  .object + .object {
    margin-top: 6px;
  }

  h3,
  h4 {
    margin: 0 0 var(--space-1);
    font-size: 0.8125rem;
    font-weight: 600;
  }

  h4 {
    margin-top: var(--space-2);
    color: var(--color-text-muted);
  }

  .kind,
  .from {
    font-size: 0.6875rem;
    font-weight: 400;
    color: var(--color-text-muted);
  }

  dl {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 2px var(--space-3);
    margin: 0;
    font-size: 0.8125rem;
  }

  dt {
    color: var(--color-text-muted);
  }

  dd {
    margin: 0;
    font-variant-numeric: tabular-nums;
    text-align: right;
    user-select: text;
    overflow-wrap: anywhere;
  }

  .swatch {
    display: inline-block;
    width: 0.75em;
    height: 0.75em;
    margin-right: 4px;
    border: 1px solid var(--color-border);
    vertical-align: -1px;
  }

  .source {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    border: 0;
    background: none;
    color: var(--color-accent);
    font: inherit;
    cursor: pointer;
  }

  .source:hover {
    text-decoration: underline;
  }

  .error {
    color: #e03131;
    text-align: left;
  }
</style>
